import React from "react";

/**
 * MarkdownLite renders our simple content format:
 *   ## Heading    -> h2
 *   ### Heading   -> h3
 *   - bullet      -> ul/li
 *   1. numbered   -> ol/li
 *   **bold**      -> <strong>
 *   Blank line separates paragraphs.
 *   Line starting with "> " becomes a quoted callout.
 */
export default function MarkdownLite({ text = "" }) {
  const blocks = parseBlocks(text);
  return (
    <div className="prose-content space-y-5 text-slate-700 leading-relaxed">
      {blocks.map((b, i) => renderBlock(b, i))}
    </div>
  );
}

function parseBlocks(text) {
  const lines = String(text).replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let buf = [];
  const flushParagraph = () => {
    if (buf.length) {
      blocks.push({ type: "p", lines: buf.slice() });
      buf = [];
    }
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.trim() === "") {
      flushParagraph();
      i++;
      continue;
    }
    if (line.startsWith("## ")) {
      flushParagraph();
      blocks.push({ type: "h2", text: line.slice(3).trim() });
      i++;
      continue;
    }
    if (line.startsWith("### ")) {
      flushParagraph();
      blocks.push({ type: "h3", text: line.slice(4).trim() });
      i++;
      continue;
    }
    if (line.startsWith("> ")) {
      flushParagraph();
      blocks.push({ type: "quote", text: line.slice(2).trim() });
      i++;
      continue;
    }
    if (/^[-*]\s+/.test(line)) {
      flushParagraph();
      const items = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*]\s+/, ""));
        i++;
      }
      blocks.push({ type: "ul", items });
      continue;
    }
    if (/^\d+\.\s+/.test(line)) {
      flushParagraph();
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ""));
        i++;
      }
      blocks.push({ type: "ol", items });
      continue;
    }
    buf.push(line);
    i++;
  }
  flushParagraph();
  return blocks;
}

function renderInline(text) {
  // Escape and then re-inject strong for **bold**.
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((chunk, i) => {
    if (chunk.startsWith("**") && chunk.endsWith("**")) {
      return (
        <strong key={i} className="font-semibold text-slate-900">
          {chunk.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={i}>{chunk}</React.Fragment>;
  });
}

function renderBlock(b, i) {
  switch (b.type) {
    case "h2":
      return (
        <h2 key={i} className="text-2xl md:text-3xl font-extrabold text-slate-900 mt-8 mb-3">
          {renderInline(b.text)}
        </h2>
      );
    case "h3":
      return (
        <h3 key={i} className="text-xl font-bold text-slate-900 mt-6 mb-2">
          {renderInline(b.text)}
        </h3>
      );
    case "quote":
      return (
        <blockquote
          key={i}
          className="border-l-4 border-orange-400 bg-orange-50 rounded-r-md px-4 py-3 text-slate-700 italic"
        >
          {renderInline(b.text)}
        </blockquote>
      );
    case "ul":
      return (
        <ul key={i} className="list-disc pl-6 space-y-1.5 text-slate-700">
          {b.items.map((it, k) => (
            <li key={k}>{renderInline(it)}</li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol key={i} className="list-decimal pl-6 space-y-1.5 text-slate-700">
          {b.items.map((it, k) => (
            <li key={k}>{renderInline(it)}</li>
          ))}
        </ol>
      );
    case "p":
    default:
      return (
        <p key={i} className="text-slate-700 leading-relaxed">
          {b.lines.map((ln, k) => (
            <React.Fragment key={k}>
              {renderInline(ln)}
              {k < b.lines.length - 1 ? <br /> : null}
            </React.Fragment>
          ))}
        </p>
      );
  }
}
